<template>
    <v-layout class="hotel-page" column>
        <teleport v-if="isMobile" to="#under-header">
            <hotel-info-mobile-tabs v-model="mobileTabs" />
        </teleport>
        <v-container fluid>
            <hotel-breadcrumbs v-if="!isMobile" :search-request="searchRequest" />
            <v-row class="hotel-page__main-row">
                <v-col v-show="!isMobile || (isMobile && mobileTabs === 0)" md="8">
                    <hotel-info
                        :offer-prop="offer"
                        :search-request="searchRequest"
                        class="mb-3"
                        @show-map-tab="showMapTab"
                    />
                    <hotel-info-time v-if="isHotelInfoTimeTopPos" card class="mb-5" />
                    <component
                        :is="!$breakpoint.smAndDown ? 'product-gallery' : 'product-gallery-mobile'"
                        :images-data="info.images.image"
                        class="mb-3"
                    />
                    <client-only>
                        <hotel-info-room-offers-wrapper
                            v-if="!isMobile && $route.name !== 'hotelInfo'"
                            class="mb-5"
                            :search-request="searchRequest"
                            :offer="offer"
                        />
                    </client-only>
                    <div class="hotel-page__descr-blocks">
                        <hotel-info-descriptions class="mb-5" />
                        <hotel-info-time v-if="isMobile && !isHotelInfoTimeTopPos" card class="mb-5" />
                        <hotel-amenities :info="info" class="mb-5" />
                        <hotel-info-services class="mb-5" />
                    </div>
                    <similar-hotels
                        v-if="!isMobile && $route.name !== 'hotelInfo'"
                        :search-request="searchRequest"
                        :city="city"
                        class="mb-5"
                    />
                    <recent-view-hotels
                        v-if="!isMobile && $route.name !== 'hotelInfo'"
                        :search-request="searchRequest"
                        :offer="offer"
                        class="mb-5"
                    />
                    <view-offers-button v-if="isMobile && $route.name !== 'hotelInfo'" @show-offers="mobileTabs = 1" />
                </v-col>
                <v-col v-show="!isMobile || (isMobile && mobileTabs === 1)" md="4">
                    <slot name="sidebar" />
                    <hotel-info-search-summary v-if="$route.name !== 'hotelInfo'" :search-request="searchRequest" />
                    <hotel-info-time v-if="!isMobile && !isHotelInfoTimeTopPos" card class="mb-5" />
                    <client-only>
                        <hotel-info-room-offers-wrapper
                            v-if="isMobile && $route.name !== 'hotelInfo'"
                            class="mb-5"
                            :search-request="searchRequest"
                            :offer="offer"
                        />
                    </client-only>
                    <div v-if="!isMobile">
                        <client-only>
                            <hotel-info-map v-if="offer" :offer="offer" :info="info" :city="city" location-filter />
                        </client-only>
                        <hotel-info-relative-positions />
                    </div>
                </v-col>
                <v-col v-show="isMobile && mobileTabs === 2" :cols="12">
                    <client-only>
                        <hotel-info-map v-if="offer" :offer="offer" :info="info" :city="city" location-filter />
                    </client-only>
                </v-col>
            </v-row>
        </v-container>
    </v-layout>
</template>

<script lang="ts">
    import {Component, mixins, Setup, Watch} from 'vue-facing-decorator'
    import {decodeSupplierCode, isEqual} from '@/utils/helpers'
    import HotelInfo from '~src/components/info/hotelInfo.src'
    import HotelInfoSearchSummary from '@/components/info/HotelInfoSearchSummary.vue'
    import HotelInfoRoomOffersWrapper from '@/components/info/offers/HotelInfoRoomOffersWrapper.vue'
    import HotelInfoTime from '@/components/info/HotelInfoTime.vue'
    import HotelInfoRelativePositions from '@/components/info/HotelInfoRelativePositions.vue'
    import HotelInfoServices from '~src/components/info/hotelInfoServices.src'
    import HotelInfoDescriptions from '@/components/info/HotelInfoDescriptions.vue'
    import ViewOffersButton from '@/components/snippets/ViewOffersButton.vue'
    import {HotelStoreMixin} from '@/mixins/HotelStoreMixin.vue'
    import ProductGallery from '@/components/info/ProductGallery.vue'
    import ProductGalleryMobile from '@/components/info/ProductGalleryMobile.vue'
    import {HotelPageMixin} from '@/components/hotels/mixins/HotelPageMixin.vue'
    import HotelBreadcrumbs from '@/components/hotels/HotelBreadcrumbs.vue'
    import {searchRequest} from '~src/utils/hotels/hotels-blank-states.src'
    import OverlayProgress from '@/components/OverlayProgress.vue'
    import HotelInfoMobileTabs from '~src/components/hotels/info/snippets/hotelInfoMobileTabs.src'
    import {HotelDataPageMixin} from '~src/pages/hotels/mixins/hotelDataPageMixin.src'
    import {AUTH_EVENT, CHANGE_LOCALE_EVENT, EventBus} from '@/utils/event-bus'
    import SimilarHotels from '~src/components/search/offers/SimilarHotels.vue'
    import RecentViewHotels from '@/components/search/offers/RecentViewHotels.vue'
    import {useAccommodationRuntimeData} from '~/composables/hotel'
    import HotelInfoMap from '~src/components/info/hotelInfoMap.src'
    import HotelAmenities from '~src/components/info/HotelAmenities.vue'
    import type {RouteLocationNormalized} from 'vue-router'
    import useHotelPage from '~/composables/useHotelPage'

    // TODO [P4] Need additional check and refactoring
    @Component({
        components: {
            OverlayProgress,
            HotelBreadcrumbs,
            ViewOffersButton,
            HotelInfoServices,
            HotelInfoRelativePositions,
            HotelInfo,
            HotelInfoSearchSummary,
            HotelInfoRoomOffersWrapper,
            HotelInfoMap,
            HotelInfoTime,
            HotelInfoDescriptions,
            ProductGallery,
            ProductGalleryMobile,
            HotelInfoMobileTabs,
            SimilarHotels,
            RecentViewHotels,
            HotelAmenities,
        },
        async setup() {
            await useHotelPage()
        },
    })
    export class HotelPage extends mixins(HotelStoreMixin, HotelDataPageMixin, HotelPageMixin) {
        @Setup(() => useOfferExpired()) showOfferExpired: Function

        mobileTabs = 0

        @Watch('$route')
        async onQueryChange(route: RouteLocationNormalized) {
            if (route.name !== 'hotel') return
            if (!(await this.checkSearchDate('hotel'))) return
            await hotelsRuntimeStore.singleHotelSearch(this.searchRequest)
        }

        async mounted() {
            EventBus.$on('OFFER_EXPIRED_EVENT', () => this.showOfferExpired(this.refreshExpiredOffers))
            EventBus.$on(AUTH_EVENT, this.refreshExpiredOffers)
            EventBus.$on(CHANGE_LOCALE_EVENT, this.refreshData)
            await this.loadOffers()
        }

        beforeUnmount() {
            EventBus.$off('OFFER_EXPIRED_EVENT')
            EventBus.$off(AUTH_EVENT, this.refreshExpiredOffers)
            EventBus.$off(CHANGE_LOCALE_EVENT, this.refreshData)
        }

        async loadOffers() {
            await this.$store.restored
            if (!(await this.checkSearchDate('hotel'))) return
            if (!isEqual(hotelsStore.singleHotelSearchRequest, this.searchRequest)) {
                await hotelsRuntimeStore.singleHotelSearch(this.searchRequest)
            }
        }

        beforeRouteLeave(to, from, next) {
            hotelsRuntimeStore.stopSingleHotelSearch()
            next()
        }

        async refreshData() {
            const route = useRoute()
            await useAccommodationRuntimeData(route, +route.query.cityId || hotelsRuntimeStore.routeMap.cityId, true)
            await this.refreshExpiredOffers()
        }

        async refreshExpiredOffers() {
            await hotelsRuntimeStore.singleHotelSearch(this.searchRequest)
        }

        showMapTab() {
            this.mobileTabs = 2
        }

        get searchRequest() {
            const rq = Object.assign(searchRequest(), this.$route.query)
            if (typeof rq.rooms === 'string') {
                rq.rooms = [decodeURIComponent(rq.rooms)]
            } else {
                rq.rooms = rq.rooms.map(room => decodeURIComponent(room))
            }
            rq.convertToCurrency = persistentStore.currency
            rq.cityId = this.cityId || hotelsRuntimeStore.routeMap.cityId
            if (this.hotelId) {
                rq.hotelId = this.hotelId
            }
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            //For search from suggestHotels (by hotelId)
            if (rq.hotelId && !rq.supplierCode) {
                delete rq.supplierCode
                delete rq.hotelCode
                delete rq.cityCode
            } else {
                if (this.$conf.public.searchAccommodationByDistance) {
                    delete rq.distance
                    delete rq.latitude
                    delete rq.longitude
                }
                if (rq.supplierCode) {
                    rq.supplierCode = decodeSupplierCode(rq.supplierCode)
                }
            }
            rq.freeOnly = rq.freeOnly === 'true' || rq.freeOnly === true

            return rq
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get hotelGalleryComponent() {
            return this.isMobile ? 'hotel-gallery-mobile' : 'hotel-gallery'
        }

        get info() {
            return {
                ...hotelsRuntimeStore.hotelInfoResponse.hotelDescriptionInfo,
                supplierCode: hotelsRuntimeStore.hotelInfoRequest.supplierCode,
                ...(this.$route.query.own && {own: true}),
                ...(this.offer?.internalNote && {internalNote: this.offer?.internalNote}),
            }
        }

        get city() {
            return hotelsRuntimeStore.city
        }

        get hotelInfoRequest() {
            let {supplierCode} = this.$route.query
            const {cityCode, hotelCode} = this.$route.query
            const hotelId = this.hotelId
            if (supplierCode) supplierCode = decodeSupplierCode(supplierCode)
            return supplierCode && cityCode && hotelCode ? {supplierCode, cityCode, hotelCode} : {hotelId}
        }

        get offer() {
            return hotelsStore.singleHotelSearchResponse.offers.length
                ? hotelsStore.singleHotelSearchResponse.offers[0]
                : null
        }

        get hotelId() {
            return parseInt(this.$route.query.hotelId, 10) || hotelsRuntimeStore.routeMap.hotelId || undefined
        }

        get isHotelInfoTimeTopPos() {
            return false
        }
    }

    export default HotelPage
</script>
